<template>
  <v-card
    :disabled="isLoading"
    style="border-radius: 0 !important;"
    @keyup.enter="doSignUp"
  >
    <v-card-title
      class="primary--text font-weight-thin"
    >
      Get started with {{ companyName() }}
    </v-card-title>
    <v-card-text>
      <v-alert
        v-if="errorMessages.length > 0 && submitAttempted"
        outlined
        type="error"
        prominent
        border="left"
      >
        <ul>
          <template v-for="(error) in errorMessages">
            <li>{{ error }}</li>
          </template>
        </ul>
      </v-alert>
      <v-row>
        <v-col cols="12">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="model.companyName"
                :success="!checkModelErrors('companyName') && model.companyName !== ''"
                :error-messages="checkModelErrors('companyName')"
                outlined
                type="text"
                label="Company Name"
                dense
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="model.firstName"
                :success="!checkModelErrors('firstName') && model.firstName !== ''"
                :error-messages="checkModelErrors('firstName')"
                outlined
                type="text"
                label="First Name"
                dense
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="model.lastName"
                :success="!checkModelErrors('lastName') && model.lastName !== ''"
                :error-messages="checkModelErrors('lastName')"
                outlined
                type="text"
                label="Last Name"
                dense
              />
            </v-col>
            <v-col cols="12">
              <international-phone-number
                @change="onPhoneNumberChange"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="model.email"
                :success="validEmail"
                outlined
                type="email"
                label="Email"
                :error-messages="!validEmail && model.email !== '' ? 'Invalid email' : ''"
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="model.password"
                :success="doPasswordsMatch && model.confirmPassword !== ''"
                :error="!doPasswordsMatch"
                outlined
                type="password"
                label="Password"
                dense
                hint="Passwords must contain at least 6 characters"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="model.confirmPassword"
                :success="doPasswordsMatch && model.confirmPassword !== ''"
                outlined
                type="password"
                label="Confirm password"
                :error-messages="doPasswordsMatch ? '' : 'Passwords must match'"
                dense
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-divider class="mb-5" />

      <v-btn
        :loading="isLoading"
        block
        color="primary"
        class="white--text"
        @click="doSignUp"
      >
        Create Account
      </v-btn>
    </v-card-text>
  </v-card>
</template>

<script>
// @ is an alias to /src
import { mapActions } from 'vuex'
import { isEmpty, get } from 'lodash'
import InternationalPhoneNumber from "./Common/InternationalPhoneNumber"

export default {
  components: { InternationalPhoneNumber },
  data() {
    return {
      isLoading: false,
      submitAttempted: false,
      modelErrors: {},
      model: {
        companyName: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        password: '',
        confirmPassword: '',
      }
    }
  },
  computed: {
    errorMessages() {
      return Object.values(this.modelErrors)
        .filter(e => !isEmpty(e))
    },
    checkModelErrors() {
      return function (property) {
        return get(this.modelErrors, property, '')
      }
    },
    doPasswordsMatch() {
      return this.model.password === this.model.confirmPassword
        || this.model.confirmPassword === ''
    },
    validEmail() {
      const regExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return regExp.test(this.model.email)
    }
  },
  watch: {
    model: {
      handler() {
        this.validateForm()
      },
      deep: true
    },
  },
  methods: {
    ...mapActions([ 'createNewTenantWithNewUser', 'login' ]),
    companyName() {
      return process.env.VUE_APP_COMPANY_NAME
    },
    onPhoneNumberChange(phone) {
      this.model = {
        ...this.model,
        phoneNumber: phone.number
      }
    },
    setModelError(key, value) {
      this.modelErrors = {
        ...this.modelErrors,
        [ key ]: value
      }
    },
    isModelValid() {
      for (const key in this.modelErrors) {
        if (this.modelErrors[ key ] !== null && this.modelErrors[ key ] !== '')
          return false
      }
      return true
    },
    validateForm() {
      isEmpty(this.model.companyName)
        ? this.setModelError('companyName', 'Company Name is required')
        : this.setModelError('companyName', '')

      isEmpty(this.model.firstName)
        ? this.setModelError('firstName', 'First Name is required')
        : this.setModelError('firstName', '')

      isEmpty(this.model.lastName)
        ? this.setModelError('lastName', 'Last Name is required')
        : this.setModelError('lastName', '')

      isEmpty(this.model.phoneNumber)
        ? this.setModelError('phoneNumber', 'Valid Phone Number is required')
        : this.setModelError('phoneNumber', '')

      isEmpty(this.model.email) || !this.validEmail
        ? this.setModelError('email', 'Valid email is required')
        : this.setModelError('email', '')

      isEmpty(this.model.password) || isEmpty(this.model.confirmPassword) || !this.doPasswordsMatch
        ? this.setModelError('password', 'Passwords must match')
        : this.setModelError('password', '')
    },
    doSignUp() {
      this.submitAttempted = true
      this.validateForm()

      if (this.isModelValid()) {
        const payload = { ...this.model }
        this.isLoading = true
        this.createNewTenantWithNewUser(payload)
          .then(async () => {
            // Log the user in after creating a new tenant
            const loginPayload = {
              identity: this.model.email,
              password: this.model.password
            }

            this.login(loginPayload)

            this.isLoading = false
            this.$router.push({ name: 'admin-home' })

          })
          .catch(e => {this.$store.dispatch('snackError', e)})
          .finally(() => {
            this.isLoading = false
          })
      } else {
        console.log('invalid')
      }
    }
  }
}
</script>
